import { useEffect, useState } from "react";
import { InfoHint } from "../components/InfoHint";
import { ScadenzaBadge } from "../components/ScadenzaBadge";
import { StarRating } from "../components/StarRating";
import { api, markSectionSeen } from "../lib/api";
import { highlightMatch } from "../lib/highlightMatch";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { Card } from "../components/ui/card";
import { SharedCard, getBandTicket } from "../components/SharedCard";
import { Button } from "../components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { toast } from "sonner";
import { StatusBadge, PrioritaBadge } from "../components/StatusBadge";
import { CategoryIcon } from "../components/CategoryIcon";
import { AlertTriangle, ChevronRight, ChevronDown, Filter, PlusCircle, Trash2, UserCheck, UserX, Search, Clock3, CalendarClock, Copy, Pencil, MessageCircle, Download, FileText, Mail, Megaphone, ShieldAlert, CheckCircle2 } from "lucide-react";

const STATI_ATTIVE = [
  { value: "Nuova", label: "Nuova - Da Affidare" },
  { value: "Affidata", label: "Affidata" },
  { value: "In lavorazione", label: "In lavorazione" },
  { value: "Eseguito", label: "Eseguito" },
  { value: "in_revisione", label: "In revisione" }, // Iter122fh · filtro speciale: bool flag
];
const STATI_ARCHIVIO = ["Chiusa", "Annullata"];
const fmtScad = (iso) => (iso ? new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric" }) : null);
const initials = (name) => (name || "").trim().split(/\s+/).slice(0, 2).map((w) => (w[0] || "").toUpperCase()).join("");

// Filtro select stabile (definito a livello modulo per evitare rimonti ad ogni render)
function FilterSelect({ field, label, options, filters, setFilters }) {
  return (
    <select data-testid={`filter-${field}`} value={filters[field]}
      onChange={(e) => setFilters((f) => ({ ...f, [field]: e.target.value }))}
      className="h-10 px-3 border-2 border-slate-300 rounded-lg bg-white text-sm w-full sm:w-[160px] truncate">
      <option value="">{label}</option>
      {options.map((o) => <option key={o.value ?? o} value={o.value ?? o}>{o.label ?? o}</option>)}
    </select>
  );
}

export default function Tickets() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [condomini, setCondomini] = useState([]);
  const [fornitori, setFornitori] = useState([]);
  const [meta, setMeta] = useState({ categorie: [], urgenze: [], priorita: [] });
  const [vista, setVista] = useState("attive");
  const [q, setQ] = useState("");
  const [filters, setFilters] = useState({ stato: "", condominio_id: "", priorita: "", categoria: "", fornitore_id: "", affido: "" });
  // Iter122jw · Filtro rapido "Scaduti" (client-side) — manutenzioni con in_ritardo=true, non archiviate
  // Iter123k188 · Filtro rapido aggiuntivo "Eseguiti" (stato = Eseguito) · chip verde
  const [filtroRapido, setFiltroRapido] = useState("");
  const [toDelete, setToDelete] = useState(null);
  const [fornFilterOpen, setFornFilterOpen] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [fornFilterSearch, setFornFilterSearch] = useState("");

  const load = () => {
    const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
    params.archiviate = vista === "archivio";
    // Iter122fh · Filtro "In revisione" è un bool (non uno stato standard) → convertiamo
    if (params.stato === "in_revisione") {
      delete params.stato;
      params.in_revisione = true;
    }
    api.get("/tickets", { params }).then((r) => setTickets(r.data)).catch(() => {});
  };

  const confirmDelete = async () => {
    if (!toDelete) return;
    try {
      await api.delete(`/tickets/${toDelete.id}`);
      toast.success(`Manutenzione ${toDelete.numero} eliminata`);
      setToDelete(null);
      load();
    } catch {
      toast.error("Eliminazione non riuscita");
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, vista]);
  useEffect(() => {
    api.get("/meta").then((r) => setMeta(r.data)).catch(() => {});
    // Iter122at · azzera il badge sidebar "manutenzioni" per il condòmino
    if (user.role === "condomino") { markSectionSeen("manutenzioni"); }
    if (user.role === "admin") {
      api.get("/condomini").then((r) => setCondomini(r.data)).catch(() => {});
      api.get("/fornitori", { params: { limit: 1000 } }).then((r) => setFornitori(r.data.fornitori || [])).catch(() => {});
    } else if (user.role === "fornitore") {
      // Per il fornitore i condomìni selezionabili sono quelli delle sue manutenzioni
      api.get("/tickets").then((r) => {
        const map = new Map();
        r.data.forEach((t) => { if (t.condominio_id) map.set(t.condominio_id, t.condominio_nome); });
        setCondomini(Array.from(map, ([id, nome]) => ({ id, nome })).sort((a, b) => a.nome.localeCompare(b.nome)));
      }).catch(() => {});
    }
  }, [user.role]);

  const fornFilterLabel = filters.fornitore_id === "__none__"
    ? "— Non assegnate —"
    : (fornitori.find((f) => f.id === filters.fornitore_id)?.ragione_sociale || "Tutti i fornitori");
  const fornFilterList = fornitori.filter((f) => {
    const s = fornFilterSearch.trim().toLowerCase();
    if (!s) return true;
    return [f.ragione_sociale, f.settore, f.citta].filter(Boolean).join(" ").toLowerCase().includes(s);
  });

  const ql = q.trim().toLowerCase();
  // Iter122jw · Applica filtro rapido "Scaduti" / "Eseguiti" (client-side) prima della ricerca testuale
  let preScaduti = tickets;
  if (filtroRapido === "__SCADUTI__") {
    preScaduti = tickets.filter((t) => t.in_ritardo && !STATI_ARCHIVIO.includes(t.stato));
  } else if (filtroRapido === "__ESEGUITI__") {
    preScaduti = tickets.filter((t) => t.stato === "Eseguito");
  }
  const visibili = !ql ? preScaduti : preScaduti.filter((t) =>
    [t.numero, t.titolo, t.condominio_nome, t.categoria, t.fornitore_nome, t.condomino_nome, t.segnalante_nome]
      .filter(Boolean).join(" ").toLowerCase().includes(ql));

  // Iter112 · export CSV/PDF per admin (analogo a Preventivi)
  // Iter122im · nel CSV inclusi Segnalante, Unità, Telefono principale, Referenti dalla scheda
  const _fmtReferenti = (t) => {
    const rr = Array.isArray(t.contatti_referenti) ? t.contatti_referenti : [];
    return rr
      .filter((c) => (c?.nome || "").trim())
      .map((c) => {
        const parts = [c.nome];
        if ((c.unita || "").trim()) parts.push(`[${c.unita}]`);
        if ((c.ruolo || "").trim()) parts.push(`(${c.ruolo})`);
        if ((c.telefono || "").trim()) parts.push(`— ${c.telefono}`);
        return parts.join(" ");
      })
      .join(" | ");
  };

  const esportaCsv = () => {
    const rows = [[
      "Numero", "Stato", "Priorità", "Categoria", "Condominio", "Fornitore", "Titolo", "Scadenza",
      "Segnalante", "Unità", "Telefono principale", "Referenti",
    ]];
    visibili.forEach((t) => rows.push([
      t.numero, t.stato, t.priorita || "", t.categoria || "",
      t.condominio_nome || "", t.fornitore_nome || "", (t.titolo || "").replace(/\s+/g, " "),
      t.data_scadenza ? new Date(t.data_scadenza).toLocaleDateString("it-IT") : "",
      t.segnalante_nome || "", t.unita || "",
      t.telefono_contatto || "",
      _fmtReferenti(t),
    ]));
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(";")).join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `manutenzioni_${vista === "archivio" ? "archivio" : "elenco"}_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const esportaPdf = async () => {
    try {
      const params = { archiviate: vista === "archivio" };
      Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
      if (ql) params.q = q.trim();
      const r = await api.get("/tickets/export/pdf", { params, responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(r.data);
      a.download = `manutenzioni_${vista === "archivio" ? "archivio" : "elenco"}_${new Date().toISOString().slice(0, 10)}.pdf`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch { toast.error("Export PDF non riuscito"); }
  };

  // Iter123k262 · Invio email di riepilogo attività aperte al fornitore
  // filtrato. Riusa l'endpoint `/fornitori/{fid}/invia-riepilogo` che allega
  // in automatico PDF manutenzioni + preventivi aperti del fornitore.
  const [emailInv, setEmailInv] = useState(false);
  const inviaRiepilogoEmailFornitore = async () => {
    const fid = filters.fornitore_id;
    if (!fid || fid === "__none__") return;
    const forn = fornitori.find((f) => f.id === fid);
    if (!(forn?.email || "").trim()) {
      toast.error("Il fornitore selezionato non ha un indirizzo email configurato");
      return;
    }
    const nome = forn.ragione_sociale || "fornitore";
    if (!window.confirm(
      `Inviare a ${nome} (${forn.email}) il riepilogo delle attività aperte via email?`
    )) return;
    setEmailInv(true);
    try {
      const { data } = await api.post(
        `/fornitori/${fid}/invia-riepilogo`, null,
        { params: { include_manutenzioni: true, include_preventivi: true } },
      );
      const parts = [];
      if (data.ticket_count) parts.push(`${data.ticket_count} manutenzion${data.ticket_count === 1 ? "e" : "i"}`);
      if (data.preventivo_count) parts.push(`${data.preventivo_count} preventiv${data.preventivo_count === 1 ? "o" : "i"}`);
      toast.success(`Riepilogo inviato a ${data.sent_to}${parts.length ? " (" + parts.join(" + ") + ")" : ""}`);
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Invio email non riuscito");
    } finally { setEmailInv(false); }
  };

  return (
    <div className="max-w-7xl">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
        <div className="flex items-center gap-2">
          <h1 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight">
            {user.role === "condomino" ? "Le mie manutenzioni" : "Manutenzioni"}
          </h1>
          <InfoHint section="segnalazioni" />
        </div>
        {user.role !== "fornitore" && (
          <Button data-testid="tickets-new-button" onClick={() => navigate("/nuova")}
            className="h-12 px-5 text-base font-bold bg-brand hover:bg-brand-dark">
            <PlusCircle className="w-5 h-5 mr-2" /> {user.role === "condomino" ? "Segnala una manutenzione" : "Nuova"}
          </Button>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="inline-flex rounded-xl border-2 border-brand/20 p-1 bg-white" data-testid="tickets-vista-toggle">
          {[["attive", "Attive"], ["archivio", "Archivio"]].map(([v, l]) => (
            <button key={v} data-testid={`tickets-vista-${v}`}
              onClick={() => { setVista(v); setFilters((f) => ({ ...f, stato: "", affido: "" })); setFiltroRapido(""); }}
              className={`px-3 h-9 rounded-lg text-xs sm:text-sm font-bold transition-colors ${vista === v ? "bg-brand text-white" : "text-brand hover:bg-[#F5EAEC]"}`}>
              {l}
            </button>
          ))}
        </div>
        {/* Iter122jw · Filtro rapido "Scaduti" (rosso) — manutenzioni in ritardo non chiuse */}
        {vista === "attive" && (() => {
          const scadutiCount = tickets.filter((t) => t.in_ritardo && !STATI_ARCHIVIO.includes(t.stato)).length;
          const on = filtroRapido === "__SCADUTI__";
          return (
            <button data-testid="filtro-scaduti"
              onClick={() => setFiltroRapido(on ? "" : "__SCADUTI__")}
              className={`px-2.5 sm:px-3.5 h-9 rounded-lg text-xs sm:text-sm font-semibold border-2 transition-colors inline-flex items-center gap-1.5 ${on ? "bg-red-600 text-white border-red-600" : "bg-white border-red-500 text-red-700 hover:bg-red-50"}`}>
              <CalendarClock className="w-3.5 h-3.5" /> Scaduti{scadutiCount > 0 ? ` (${scadutiCount})` : ""}
            </button>
          );
        })()}
        {/* Iter123k188 · Filtro rapido "Eseguiti" (verde) — stato = Eseguito */}
        {vista === "attive" && (() => {
          const esegCount = tickets.filter((t) => t.stato === "Eseguito").length;
          const on = filtroRapido === "__ESEGUITI__";
          return (
            <button data-testid="filtro-eseguiti"
              onClick={() => setFiltroRapido(on ? "" : "__ESEGUITI__")}
              className={`px-2.5 sm:px-3.5 h-9 rounded-lg text-xs sm:text-sm font-semibold border-2 transition-colors inline-flex items-center gap-1.5 ${on ? "bg-emerald-600 text-white border-emerald-600" : "bg-white border-emerald-500 text-emerald-700 hover:bg-emerald-50"}`}>
              <CheckCircle2 className="w-3.5 h-3.5" /> Eseguiti{esegCount > 0 ? ` (${esegCount})` : ""}
            </button>
          );
        })()}
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input data-testid="tickets-search" value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Cerca per numero, titolo, condominio…"
            className="h-11 w-full pl-9 pr-3 border-2 border-slate-300 rounded-lg text-base" />
        </div>
        {user.role === "admin" && (
          <>
            <button type="button" data-testid="tickets-export-csv" onClick={esportaCsv} disabled={visibili.length === 0}
              className="h-11 px-3 rounded-lg border-2 border-slate-300 bg-white text-sm font-semibold inline-flex items-center gap-1.5 disabled:opacity-40">
              <Download className="w-4 h-4" /> CSV
            </button>
            <button type="button" data-testid="tickets-export-pdf" onClick={esportaPdf} disabled={visibili.length === 0}
              className="h-11 px-3 rounded-lg border-2 border-slate-300 bg-white text-sm font-semibold inline-flex items-center gap-1.5 disabled:opacity-40">
              <FileText className="w-4 h-4" /> PDF
            </button>
            {/* Iter123k262 · Invia via email il riepilogo delle attività aperte
                 al fornitore attualmente filtrato. Visibile solo se è stato
                 scelto un fornitore reale (non "__none__" = senza fornitore). */}
            {filters.fornitore_id && filters.fornitore_id !== "__none__" && (
              <button type="button" data-testid="tickets-send-email-fornitore"
                onClick={inviaRiepilogoEmailFornitore} disabled={emailInv}
                title={`Invia a ${fornitori.find((f) => f.id === filters.fornitore_id)?.ragione_sociale || "fornitore"} un'email di riepilogo attività aperte`}
                className="h-11 px-3 rounded-lg border-2 border-amber-400 bg-amber-50 text-amber-900 text-sm font-semibold inline-flex items-center gap-1.5 disabled:opacity-40 hover:bg-amber-100">
                <Mail className="w-4 h-4" /> {emailInv ? "Invio…" : "Invia email"}
              </button>
            )}
          </>
        )}
        {vista === "archivio" && <span className="text-sm text-slate-500" data-testid="tickets-archivio-hint">Manutenzioni chiuse o annullate</span>}
      </div>

      {user.role !== "condomino" && (
        <Card className="p-4 mb-4 border-2">
          <button type="button" data-testid="filters-toggle" onClick={() => setFiltersOpen((o) => !o)}
            className="flex items-center gap-2 w-full text-slate-600 font-semibold">
            <Filter className="w-5 h-5" /> Filtri
            {Object.values(filters).filter(Boolean).length > 0 && (
              <span data-testid="filters-active-count" className="text-xs font-bold px-2 py-0.5 rounded-full bg-brand text-white">{Object.values(filters).filter(Boolean).length}</span>
            )}
            <ChevronDown className={`w-4 h-4 ml-auto transition-transform ${filtersOpen ? "rotate-180" : ""}`} />
          </button>
          {filtersOpen && (
          <div className="flex flex-wrap gap-3 mt-3">
            <FilterSelect field="stato" label="Tutti gli stati" options={vista === "archivio" ? STATI_ARCHIVIO : STATI_ATTIVE} filters={filters} setFilters={setFilters} />
            {vista !== "archivio" && <FilterSelect field="affido" label="Stato affido (tutti)" options={[{ value: "da_accettare", label: "Affido da accettare" }, { value: "scaduto", label: "Affido scaduto" }]} filters={filters} setFilters={setFilters} />}
            <FilterSelect field="priorita" label="Tutte le priorità" options={meta.priorita} filters={filters} setFilters={setFilters} />
            <FilterSelect field="categoria" label="Tutte le categorie" options={meta.categorie} filters={filters} setFilters={setFilters} />
            {user.role !== "condomino" && (
              <FilterSelect field="condominio_id" label="Tutti i condomìni" options={condomini.map((c) => ({ value: c.id, label: c.nome }))} filters={filters} setFilters={setFilters} />
            )}
            {user.role === "admin" && (
              <div className="relative w-full sm:w-[200px]" data-testid="filter-fornitore-wrap">
                <button type="button" data-testid="filter-fornitore_id-toggle" onClick={() => setFornFilterOpen((o) => !o)}
                  className="h-10 px-3 border-2 border-slate-300 rounded-lg bg-white text-sm w-full flex items-center justify-between gap-2">
                  <span className={`truncate ${filters.fornitore_id ? "text-slate-800 font-semibold" : "text-slate-500"}`}>{fornFilterLabel}</span>
                  <ChevronDown className={`w-4 h-4 shrink-0 transition-transform ${fornFilterOpen ? "rotate-180" : ""}`} />
                </button>
                {fornFilterOpen && (
                  <div className="absolute z-20 mt-1 w-full sm:w-[280px] border-2 border-slate-200 rounded-lg bg-white shadow-lg overflow-hidden">
                    <div className="p-2 border-b bg-slate-50">
                      <div className="relative">
                        <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                        <input data-testid="filter-fornitore_id-search" autoFocus value={fornFilterSearch}
                          onChange={(e) => setFornFilterSearch(e.target.value)} placeholder="Cerca fornitore..."
                          className="h-10 w-full pl-9 pr-2 border-2 border-slate-200 rounded-lg text-base" />
                      </div>
                    </div>
                    <div className="max-h-64 overflow-y-auto overflow-x-hidden divide-y">
                      <button type="button" className="w-full text-left px-3 py-2.5 hover:bg-slate-50 text-base"
                        onClick={() => { setFilters((f) => ({ ...f, fornitore_id: "" })); setFornFilterOpen(false); setFornFilterSearch(""); }}>Tutti i fornitori</button>
                      <button type="button" data-testid="filter-fornitore-opt-none" className="w-full text-left px-3 py-2.5 hover:bg-slate-50 text-base text-red-700 font-semibold"
                        onClick={() => { setFilters((f) => ({ ...f, fornitore_id: "__none__" })); setFornFilterOpen(false); setFornFilterSearch(""); }}>— Non assegnate —</button>
                      {fornFilterList.slice(0, 100).map((f) => (
                        <button type="button" key={f.id} data-testid={`filter-fornitore-opt-${f.id}`}
                          onClick={() => { setFilters((fl) => ({ ...fl, fornitore_id: f.id })); setFornFilterOpen(false); setFornFilterSearch(""); }}
                          className={`w-full text-left px-3 py-2.5 hover:bg-slate-50 ${f.id === filters.fornitore_id ? "bg-[#FBEEF3]" : ""}`}>
                          <div className="font-semibold text-slate-800 truncate">{highlightMatch(f.ragione_sociale, fornFilterSearch)}</div>
                          <div className="text-sm text-slate-500 truncate">{highlightMatch([f.settore, f.citta].filter(Boolean).join(" · ") || "—", fornFilterSearch)}</div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
            <Button data-testid="filters-reset" variant="outline" className="h-10 text-sm"
              onClick={() => setFilters({ stato: "", condominio_id: "", priorita: "", categoria: "", fornitore_id: "", affido: "" })}>
              Azzera
            </Button>
          </div>
          )}
        </Card>
      )}

      <div className="space-y-2">
        {visibili.length === 0 && (
          <Card className="p-5 text-center text-slate-500 text-lg border-2" data-testid="tickets-empty">
            {vista === "archivio" ? "Nessuna manutenzione in archivio." : "Nessuna manutenzione trovata."}
          </Card>
        )}
        {visibili.map((t) => {
          const assegnato = !!(t.fornitore_id || t.fornitore_nome);
          const bordoStato = getBandTicket(t);
          return (
          <SharedCard key={t.id} band={bordoStato} data-testid={`ticket-row-${t.numero}`}
            onClick={() => navigate(`/segnalazioni/${t.id}`)}
            className="p-3 border-2 cursor-pointer hover:border-[#C98AA0] transition-colors">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-mono text-xs font-bold text-slate-500">{highlightMatch(t.numero, q)}</span>
                  <span className="font-semibold text-base text-slate-800 break-words">{highlightMatch(t.titolo, q)}</span>
                  {t.messaggi_non_letti > 0 && (
                    <span data-testid={`ticket-msg-badge-${t.numero}`} className="inline-flex items-center gap-1 text-xs font-bold px-2 py-0.5 rounded-full bg-red-600 text-white">
                      <MessageCircle className="w-3 h-3" /> {t.messaggi_non_letti}
                    </span>
                  )}
                  {/* Iter122y-b · badge "Nuova / Da accettare" — concorre al contatore della sidebar */}
                  {(() => {
                    const isForn = user.role === "fornitore";
                    const isAdmin = user.role === "admin";
                    const fornPending = isForn && t.stato === "Affidata" && !t.accettato_at && !t.visto_fornitore_at;
                    const adminPending = isAdmin && t.stato === "Nuova" && !t.visto_studio_at;
                    if (!fornPending && !adminPending) return null;
                    return (
                      <span data-testid={`ticket-nuova-badge-${t.numero}`} className="inline-flex items-center gap-1 text-xs font-bold px-2 py-0.5 rounded-full bg-red-600 text-white animate-pulse">
                        {fornPending ? "Da accettare" : "Nuova"}
                      </span>
                    );
                  })()}
                </div>
                <div className="text-sm text-slate-600 mt-0.5 break-words">
                  {highlightMatch(t.condominio_nome, q)} · {highlightMatch(t.categoria, q)}
                </div>
                {t.segnalante_nome && (
                  <div className="text-xs text-slate-600 mt-0.5 break-words inline-flex items-center gap-1" data-testid={`ticket-segnalante-${t.numero}`}>
                    <Megaphone className="w-3.5 h-3.5 text-brand shrink-0" />
                    <span>Segnalato da <b>{highlightMatch(t.segnalante_nome, q)}</b>{t.condomino_nome && t.condomino_nome !== t.segnalante_nome ? <> · Condòmino <b>{highlightMatch(t.condomino_nome, q)}</b></> : null}</span>
                  </div>
                )}
                <div className="text-sm mt-0.5" data-testid={`ticket-fornitore-${t.numero}`}>
                  {assegnato ? (
                    <span className="inline-flex items-center gap-1 text-slate-700 align-middle"><UserCheck className="w-4 h-4 text-green-600 shrink-0" /> <span className="break-words">{highlightMatch(t.fornitore_nome || "Fornitore assegnato", q)}</span></span>
                  ) : t.rifiutata ? (
                    <span data-testid={`ticket-rifiutata-${t.numero}`} className="inline-flex items-center gap-1 text-orange-700 font-semibold align-middle"><UserX className="w-4 h-4 shrink-0" /> Rifiutata - Da Riassegnare</span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-red-600 font-semibold align-middle"><UserX className="w-4 h-4 shrink-0" /> Non assegnato</span>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-0.5 shrink-0">
                {user.role === "admin" && t.stato === "Nuova" && (
                  <Button data-testid={`edit-ticket-${t.numero}`} variant="ghost" size="icon"
                    className="h-8 w-8 text-brand hover:bg-[#FBEEF3]" title="Modifica manutenzione"
                    onClick={(e) => { e.stopPropagation(); navigate(`/segnalazioni/${t.id}?edit=1`); }}>
                    <Pencil className="w-4 h-4" />
                  </Button>
                )}
                {user.role === "admin" && (
                  <Button data-testid={`delete-ticket-${t.numero}`} variant="ghost" size="icon"
                    className="h-8 w-8 text-red-600 hover:bg-red-50 hover:text-red-700"
                    onClick={(e) => { e.stopPropagation(); setToDelete(t); }}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                )}
                <ChevronRight className="w-5 h-5 text-slate-400" />
              </div>
            </div>
            <div className="flex items-center gap-1.5 flex-wrap mt-2 pt-2 border-t border-slate-100">
              <CategoryIcon categoria={t.categoria} compact />
              <PrioritaBadge priorita={t.priorita} size="sm" />
              <StatusBadge stato={t.stato} size="sm" />
              {t.has_open_sinistro && (
                <span data-testid={`ticket-sinistro-${t.numero}`} title="Sinistro aperto collegato"
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold border bg-red-50 text-red-700 border-red-300">
                  <ShieldAlert className="w-3.5 h-3.5" /> Sinistro
                </span>
              )}
              {t.duplicata_da_numero && (
                <span data-testid={`ticket-copia-${t.numero}`} title={`Copia della manutenzione ${t.duplicata_da_numero}`}
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold border bg-indigo-50 text-indigo-700 border-indigo-200">
                  <Copy className="w-3 h-3" /> Copia di {t.duplicata_da_numero}
                </span>
              )}
              {fmtScad(t.data_scadenza) && (
                <span data-testid={`ticket-scadenza-${t.numero}`}
                  className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold border ${t.in_ritardo ? "bg-red-50 text-red-700 border-red-200" : "bg-slate-50 text-slate-600 border-slate-200"}`}>
                  <CalendarClock className="w-3 h-3" /> Scad. {fmtScad(t.data_scadenza)}
                </span>
              )}
              {t.data_scadenza && ["Affidata", "In lavorazione", "Eseguito"].includes(t.stato) && (
                <ScadenzaBadge data_scadenza={t.data_scadenza} testid={`ticket-countdown-${t.numero}`} />
              )}
              {t.stato === "Affidata" && (
                <span data-testid={`ticket-affido-badge-${t.numero}`}
                  className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold border ${t.scadenza_notificata_at ? "bg-red-50 text-red-700 border-red-200" : "bg-amber-50 text-amber-700 border-amber-200"}`}>
                  <Clock3 className="w-3 h-3" /> {t.scadenza_notificata_at ? "Affido scaduto" : "Da accettare"}
                </span>
              )}
              {/* Iter122fh · Badge "IN REVISIONE" (azzurro) accanto al chip di stato quando la
                  manutenzione è stata riaperta dall'archivio (contestazione/verifica). */}
              {t.in_revisione && (
                <span data-testid={`ticket-revisione-badge-${t.numero}`}
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold border bg-sky-100 text-sky-800 border-sky-300">
                  <AlertTriangle className="w-3 h-3" /> IN REVISIONE
                </span>
              )}
              {(t.in_ritardo || t.in_scadenza) && (
                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold border ${t.in_ritardo ? "bg-red-50 text-red-700 border-red-200" : "bg-amber-50 text-amber-700 border-amber-200"}`}>
                  <AlertTriangle className="w-3 h-3" /> {t.alert_msg || (t.in_ritardo ? "In ritardo" : "In scadenza")}
                </span>
              )}
              {user.role === "admin" && t.operatore_nome && (
                <span data-testid={`ticket-operatore-${t.numero}`} title={`In gestione: ${t.operatore_nome}`}
                  className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-brand text-white text-[10px] font-bold shrink-0 ml-auto">
                  {initials(t.operatore_nome)}
                </span>
              )}
              {/* Iter122k157 · Valutazione media a stella (piccola) nella card anteprima
                  manutenzioni. Media combinata quando presenti entrambe (condòmino + Studio),
                  altrimenti la valutazione singola disponibile. Nulla se non c'è alcuna valutazione. */}
              {(() => {
                const mc = t.feedback?.media;
                const ms = t.feedback_studio?.media;
                if (mc == null && ms == null) return null;
                const media = (mc != null && ms != null)
                  ? Math.round(((mc + ms) / 2) * 10) / 10
                  : (mc != null ? mc : ms);
                return (
                  <span data-testid={`ticket-rating-${t.numero}`}
                    title={mc != null && ms != null
                      ? `Media condòmino ${mc}★ + Studio ${ms}★`
                      : `Valutazione ${mc != null ? "condòmino" : "Studio"} ${media}★`}
                    className={`inline-flex items-center gap-1 text-[11px] font-bold text-amber-700 shrink-0 ${user.role === "admin" && t.operatore_nome ? "" : "ml-auto"}`}>
                    <StarRating value={Math.round(media)} readOnly size={3} />
                    <span>{media}</span>
                  </span>
                );
              })()}
            </div>
          </SharedCard>
          );
        })}
      </div>

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-2xl">Eliminare la manutenzione?</AlertDialogTitle>
            <AlertDialogDescription className="text-base">
              Stai per eliminare definitivamente la manutenzione <b>{toDelete?.numero}</b> — "{toDelete?.titolo}".
              L'operazione non è reversibile.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="h-12">Annulla</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-ticket" onClick={confirmDelete} className="h-12 bg-red-700 hover:bg-red-800">
              Elimina definitivamente
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
